import PropTypes from 'prop-types';
import { Component } from 'react';
import { withReactHooks } from 'src/common/decorators/withReactHooks';
import { useBreakpoint } from 'src/common/hooks/useBreakpoint';
import { isXSmall } from 'src/common/utils/breakpoints';
import ContainerTitle from '../shared/ContainerTitle';
import Carousel from './Carousel';
import { imageLoadingModes } from './CarouselItems';
import css from './gallery.module.scss';
import arrangeItemsInRows from './utils/arrangeItemsInRows';
import getNumberOfPages from './utils/getNumberOfPages';
import selectGalleryBreakpoint from './utils/selectGalleryBreakpoint';

// exported for testing
export const pageSize = 6;

// The first galleries on a page are usually in view on load, so their visible tiles skip lazy
// loading, and the first gallery's lead image (usually the LCP element) is fetched first.
const eagerGalleryCount = 2;

function getImageLoading(index) {
  if (index === 0) return imageLoadingModes.priority;
  if (index < eagerGalleryCount) return imageLoadingModes.eager;
  return undefined;
}

// exported for testing
export class Gallery extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    index: PropTypes.number,
    breakpoint: PropTypes.number.isRequired,
    containerItem: PropTypes.object.isRequired,
    enableHorizontalScroll: PropTypes.bool,
  };

  state = {
    page: 0,
    pages: this.getNumOfPages(),
  };

  componentDidUpdate(prevProps) {
    const { breakpoint } = this.props;
    if (isXSmall(prevProps.breakpoint) !== isXSmall(breakpoint)) {
      const pages = this.getNumOfPages();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ page: 0, pages });
    } else if (prevProps.breakpoint !== breakpoint) {
      const pages = this.getNumOfPages();
      if (pages !== this.state.pages) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ pages });
      }
    }
  }

  getNumOfPages() {
    const { containerItem, breakpoint } = this.props;
    const { children, rowCount } = containerItem;

    return getNumberOfPages(children, pageSize, breakpoint, rowCount);
  }

  handlePageLeft = () => {
    const prevPage = this.state.page - 1;
    this.setState({ page: prevPage < 0 ? 0 : prevPage });
  };

  handlePageRight = () => {
    const { pages, page } = this.state;
    const totalPageIndex = pages - 1;
    const nextPage = page + 1;
    this.setState({
      page: nextPage > totalPageIndex ? totalPageIndex : nextPage,
    });
  };

  render() {
    const { page, pages } = this.state;
    const { id, index, containerItem, breakpoint, enableHorizontalScroll } = this.props;

    const showButtons = !enableHorizontalScroll && isXSmall(this.props.breakpoint);
    const isLeftButtonDisabled = page === 0;
    const isRightButtonDisabled = page >= pages - 1;
    const { rowCount, isExtendedInfoEnabled, children } = containerItem;
    const itemsMatrix = arrangeItemsInRows(children, rowCount);

    return (
      <div
        id={id}
        data-testid={id}
        data-testtype="gallery"
        data-testcount={itemsMatrix.length * itemsMatrix[0].length}
      >
        {containerItem.title && (
          <ContainerTitle containerItem={containerItem} className={css.galleryTitle} />
        )}
        <Carousel
          page={page}
          numRows={rowCount}
          itemsMatrix={itemsMatrix}
          breakpoint={breakpoint}
          showButtons={showButtons}
          isLeftButtonDisabled={isLeftButtonDisabled}
          isRightButtonDisabled={isRightButtonDisabled}
          isExtendedInfoEnabled={isExtendedInfoEnabled}
          handlePageLeft={this.handlePageLeft}
          handlePageRight={this.handlePageRight}
          imageLoading={getImageLoading(index)}
        />
      </div>
    );
  }
}

export default withReactHooks(() => ({ breakpoint: useBreakpoint(selectGalleryBreakpoint) }))(
  Gallery,
);
